import { useState } from 'react';
import { queryTypes, useQueryState } from 'next-usequerystate';
import { composeValidators, isRequired } from '@belong/common';
import { IconPlus, IconTrash } from '@belong/icons';
import { Text, Spinner, Form, DropdownField, Button } from '@belong/ui';
import { FieldArray } from 'formik';
import { UnitListingDetailsModel } from 'src/api/models';
import { ModalContactUsBottom } from 'src/components/modal-contact-us-bottom/modal-contact-us-bottom';
import { HOUSE_PAGE_STRINGS } from 'src/features/house-page/house-page.strings';
import {
  useGetResidentTourRequests,
  useCreateResidentTourRequest,
  useUpdateResidentTourRequest,
} from 'src/features/house-page/queries';
import { usePreQualificationGate } from 'src/features/resident-pre-qualification/use-pre-qualification-gate';
import { getPreQualificationGateMessage } from 'src/features/resident-pre-qualification/utils';
import { isTourRequestActive } from 'src/features/residents/utils';
import { useUser } from 'src/features/users/useUser';
import {
  isEstimatedShowReadyOnInFuture,
  isMilestoneAtOrAfter,
} from 'src/features/house-page/utils';
import { is } from 'superstruct';
import {
  DatesQueryParamStruct,
  TourAvailabilityFormValues,
  getDateOptions,
  getTimeOptions,
  isFutureDate,
  isOnOrAfterDate,
  transforms,
} from './utils';

type TourSendAvailabilityProps = {
  home: UnitListingDetailsModel;
  onDismiss: () => void;
  onTourRequestSent: () => void;
};

const MIN_DATES_AMOUNT = 3;

const validateDatesMinimumAmount =
  ({ datesAmount, dateIndex }: { datesAmount: number; dateIndex: number }) =>
  (value: unknown) => {
    if (
      datesAmount < MIN_DATES_AMOUNT ||
      (dateIndex < MIN_DATES_AMOUNT && isRequired(value))
    ) {
      return HOUSE_PAGE_STRINGS['tour.minDatesValidation'];
    }
  };

const getNewTimeRange = () => ({
  startOn: '',
  endOn: '',
});

const getNewDate = () => ({
  date: '',
  timeRanges: [getNewTimeRange()],
});

const isEndTimeAfterStart = (startTime: string) => (endTime: string) => {
  const [startTimeHours, startTimeMinutes] = startTime.split(':').map(Number);
  const [endTimeHours, endTimeMinutes] = endTime.split(':').map(Number);

  // `!`: destructuring the split-and-mapped array yields `number | undefined`
  // per element; the comparisons already treated them as numbers.
  if (
    startTimeHours! > endTimeHours! ||
    (startTimeHours === endTimeHours && startTimeMinutes! >= endTimeMinutes!)
  ) {
    return 'End time must be after start time';
  }
};

export function TourSendAvailability({
  home,
  onTourRequestSent,
}: TourSendAvailabilityProps) {
  const { user } = useUser();
  const { userId } = user || {};
  const { guardAfterServerError, content: preQualificationContent } =
    usePreQualificationGate({ homeId: home.basicInfo?.unitId });
  const [datesQueryParam] = useQueryState('dates', queryTypes.json());

  const validatedDatesQueryParam = is(datesQueryParam, DatesQueryParamStruct)
    ? datesQueryParam
    : undefined;

  const { data: tourRequests, isLoading: isTourRequestsLoading } =
    useGetResidentTourRequests({ enabled: !!userId });
  const homeTourRequest = tourRequests?.find(
    (tourRequest) =>
      tourRequest.homeUniqueId === home.basicInfo?.unitId &&
      isTourRequestActive(tourRequest),
  );
  // `!`: every consumer below wants a plain `string`, and this step only
  // renders inside the house page's tour modal.
  const homeTimeZone = home.basicInfo?.timeZone!;
  const { isComingSoon, estimatedShowReadyOn } = home.listingInfo ?? {};
  // Coming Soon persists through ShowReady (until Full Listing Published), but tours only open at
  // ShowReady — so the "not ready for tours yet" messaging and min-date gating below should only
  // apply pre-ShowReady, not for the whole Coming Soon window (LOVE-335).
  const isPreShowReady = !isMilestoneAtOrAfter(
    home.basicInfo?.currentMilestone,
    'ShowReady',
  );
  const showComingSoonNotice = !!isComingSoon && isPreShowReady;
  const comingSoonMinDate = showComingSoonNotice
    ? estimatedShowReadyOn
    : undefined;

  const {
    trigger: createTourRequest,
    isMutating: isCreatingTourRequest,
    error: createTourError,
  } = useCreateResidentTourRequest();
  const {
    trigger: updateTourRequest,
    isMutating: isUpdatingTourRequest,
    error: updateTourError,
  } = useUpdateResidentTourRequest(homeTourRequest?.uniqueId);

  const isMutationError = createTourError || updateTourError;

  const [submittedValues, setSubmittedValues] =
    useState<TourAvailabilityFormValues>();
  const isLoading =
    isCreatingTourRequest || isUpdatingTourRequest || isTourRequestsLoading;

  async function handleSubmit(formValues: TourAvailabilityFormValues) {
    try {
      const mappedValues = transforms.formToApi({
        formValues,
        timeZone: homeTimeZone,
      });

      if (homeTourRequest) {
        await updateTourRequest(mappedValues);
      } else {
        await createTourRequest({
          ...mappedValues,
          homeId: home.basicInfo!.unitId!,
        });
      }
      onTourRequestSent();
    } catch (e) {
      if (
        guardAfterServerError('RequestTour', e, () => handleSubmit(formValues))
      ) {
        return;
      }

      console.log(e);
    }
  }

  if (preQualificationContent) {
    return preQualificationContent;
  }

  const initialFormValues =
    submittedValues ??
    (validatedDatesQueryParam
      ? transforms.queryParamToForm({
          queryParam: validatedDatesQueryParam,
          timeZone: homeTimeZone,
        })
      : homeTourRequest
        ? transforms.apiToForm({
            model: homeTourRequest,
            timeZone: homeTimeZone,
          })
        : {
            availability: [getNewDate()],
          });

  return (
    <>
      {isLoading && <Spinner />}
      <div className="w-full">
        <div className="flex flex-col items-center gap-xs px-xs text-center">
          <Text fontWeight="semibold" variant="h2">
            {HOUSE_PAGE_STRINGS['tour.whenCanYouTour']}
          </Text>
          <Text className="max-w-lg">
            {HOUSE_PAGE_STRINGS['tour.whenCanYouTourSubtitle']}
            <br className="hidden sm:block" />{' '}
            {HOUSE_PAGE_STRINGS['tour.whenCanYouTourSubtitle2']}
          </Text>
          {showComingSoonNotice && (
            <div className="mt-md w-full rounded border border-solid border-green bg-green-light-bg p-sm text-left">
              <Text fontWeight="semibold">
                {HOUSE_PAGE_STRINGS['tour.comingSoonTourTitle']}
              </Text>
              <Text className="mt-xs">
                {estimatedShowReadyOn &&
                isEstimatedShowReadyOnInFuture(estimatedShowReadyOn)
                  ? HOUSE_PAGE_STRINGS['tour.comingSoonTourSubtitle'].replace(
                      '{date}',
                      new Date(
                        `${estimatedShowReadyOn}T12:00:00`,
                      ).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      }),
                    )
                  : null}
              </Text>
            </div>
          )}
        </div>
        <div className="mb-5xl mt-xl md:mb-0">
          {!isTourRequestsLoading && (
            <Form
              initialValues={initialFormValues}
              onSubmit={(formValues) => {
                setSubmittedValues(formValues);

                return handleSubmit(formValues);
              }}
            >
              {({ values, handleSubmit, setFieldValue }) => {
                const handleAddDate = () => {
                  setFieldValue('availability', [
                    ...values.availability,
                    getNewDate(),
                  ]);
                };

                const handleRemoveDate = (index: number) => {
                  const newAvailabilityValue = [...values.availability];
                  newAvailabilityValue.splice(index, 1);
                  setFieldValue('availability', newAvailabilityValue);
                };

                const handleAddTimeRange = (availabilityItemIndex: number) => {
                  const newAvailabilityValue = [...values.availability];
                  newAvailabilityValue[availabilityItemIndex]!.timeRanges.push(
                    getNewTimeRange(),
                  );
                  setFieldValue('availability', newAvailabilityValue);
                };

                const handleRemoveTimeRange = (
                  availabilityItemIndex: number,
                  timeRangeIndex: number,
                ) => {
                  const newAvailabilityValue = [...values.availability];
                  newAvailabilityValue[
                    availabilityItemIndex
                  ]!.timeRanges.splice(timeRangeIndex, 1);
                  setFieldValue('availability', newAvailabilityValue);
                };

                return (
                  <div>
                    <div className="mb-lg flex flex-col gap-y-2xl px-sm lg:mb-0">
                      <FieldArray
                        name="availability"
                        render={() =>
                          values.availability.map((availabilityItem, i) => {
                            return (
                              <div key={i} className="flex flex-col gap-y-xl">
                                <div className="flex items-center gap-sm">
                                  <DropdownField
                                    name={`availability.${i}.date`}
                                    label={`Tour Date ${i + 1}`}
                                    options={getDateOptions(
                                      values.availability,
                                      i,
                                      comingSoonMinDate,
                                    )}
                                    portal={false}
                                    limitPopOverHeight
                                    validate={composeValidators(
                                      validateDatesMinimumAmount({
                                        datesAmount: values.availability.length,
                                        dateIndex: i,
                                      }),
                                      isRequired,
                                      isFutureDate(homeTimeZone),
                                      ...(comingSoonMinDate
                                        ? [isOnOrAfterDate(comingSoonMinDate)]
                                        : []),
                                    )}
                                    errorMessageVisible
                                    className="grow"
                                  />
                                  <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => handleRemoveDate(i)}
                                    disabled={values.availability.length === 1}
                                    className="mt-md"
                                  >
                                    <IconTrash width={14} />
                                  </Button>
                                </div>
                                <div className="flex flex-col gap-xl">
                                  {availabilityItem.timeRanges.map(
                                    (timeRange, timeRangeIndex) => (
                                      <div
                                        className="flex items-start gap-xs md:gap-sm"
                                        key={timeRangeIndex}
                                      >
                                        <DropdownField
                                          name={`availability.${i}.timeRanges.${timeRangeIndex}.startOn`}
                                          label="Start Time"
                                          className="flex-1"
                                          options={getTimeOptions({
                                            timeZone: homeTimeZone,
                                            type: 'start',
                                            currentValue: timeRange.startOn,
                                          })}
                                          portal={false}
                                          limitPopOverHeight
                                          validate={isRequired}
                                          errorMessageVisible
                                        />
                                        <DropdownField
                                          name={`availability.${i}.timeRanges.${timeRangeIndex}.endOn`}
                                          label="End Time"
                                          className="flex-1"
                                          options={getTimeOptions({
                                            timeZone: homeTimeZone,
                                            type: 'end',
                                            startTime: timeRange.startOn,
                                            currentValue: timeRange.endOn,
                                          })}
                                          portal={false}
                                          limitPopOverHeight
                                          validate={composeValidators(
                                            isRequired,
                                            isEndTimeAfterStart(
                                              timeRange.startOn,
                                            ),
                                          )}
                                          errorMessageVisible
                                        />
                                        <Button
                                          variant="text"
                                          size="small"
                                          className="relative"
                                          style={{ marginTop: 48 }}
                                          onClick={() =>
                                            handleRemoveTimeRange(
                                              i,
                                              timeRangeIndex,
                                            )
                                          }
                                          disabled={
                                            availabilityItem.timeRanges
                                              .length === 1
                                          }
                                        >
                                          <IconTrash width={14} />
                                        </Button>
                                      </div>
                                    ),
                                  )}
                                </div>
                                <div className="flex justify-end">
                                  <Button
                                    variant="text"
                                    className="flex gap-xs"
                                    size="small"
                                    onClick={() => handleAddTimeRange(i)}
                                  >
                                    <IconPlus width={18} />
                                    Add time
                                  </Button>
                                </div>
                              </div>
                            );
                          })
                        }
                      ></FieldArray>
                      <div className="flex justify-end">
                        <Button
                          variant="text"
                          className="flex gap-xs"
                          size="small"
                          onClick={handleAddDate}
                        >
                          <IconPlus width={18} />
                          Add another date
                        </Button>
                      </div>
                      {isMutationError && (
                        <Form.ErrorMessage
                          message={
                            getPreQualificationGateMessage(isMutationError) ??
                            HOUSE_PAGE_STRINGS['tour.genericRequestError']
                          }
                        />
                      )}
                    </div>
                    <div className="my-2xl md:my-0">
                      <ModalContactUsBottom
                        cta="Send"
                        ctaSize={{ _: 'fluid', lg: 'default' }}
                        onCtaClick={handleSubmit}
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                );
              }}
            </Form>
          )}
        </div>
      </div>
    </>
  );
}
